<script setup>
import {computed, onMounted, ref} from 'vue';
import Chip from "primevue/chip";

import {usePage} from "@inertiajs/vue3";
import CommonMenuBar from "@/Components/account/CommonMenuBar.vue";
import Button from "primevue/button";
import Toast from "primevue/toast";
import DynamicDialog from "primevue/dynamicdialog";
import {useI18n} from "vue-i18n";
import {useReturnToShop} from "@/Composables/useReturnToShop";
import {useGenerateAssets} from "@/Composables/generateAssets.js";

const page = usePage();

const isDebug = computed(() => page.props.isDebug);
const user = computed(() => page.props.user);

const userRoles = computed(() => user.value?.roles?.map((role) => 'role:' + role.name + '. guard: ' + role.guard_name + ' display name:' + role.display_name) || 'Geen rol');

const props = defineProps({
    title: String,
    product: Object,
});

const product = ref(props.product);

const items = ref([])

const tsr = computed(() => {
    return page.props._tsr?.reference || '';
});

const isConfiguratorUser = page.props.isConfiguratorUser;

const {t} = useI18n()
const {returnToShop: goToCallbackUrl} = useReturnToShop();
const {generateAssetsNow} = useGenerateAssets();

const mobileMenuItems = computed(() => {
    if (!isConfiguratorUser) {
        return [];
    }

    return [
        {
            label: t('button.returnToShop'),
            icon: 'pi pi-check',
            command: goToCallbackUrl,
        },
    ];
});

onMounted(() => {
    if (window.bugsnagClient) {
        window.bugsnagClient.addMetadata('team', {
            id: page.props.team.id,
            name: page.props.team.name,
        });

        window.bugsnagClient.addMetadata('session', {
            tsr: page.props._tsr,
            callback: page.props.callbackUrl,
            product_name: page.props.product.name,
            product_sku: page.props.product.sku,
            product_id: page.props.product.id,
        });

        window.bugsnagClient.addMetadata('user', {
            email: page.props.user.email,
            id: page.props.user.id,
            name: page.props.user.name,
        });
    }
})

</script>

<template>
    <div class="configurator-layout w-full max-w-full overflow-x-hidden">
        <Toast/>
        <CommonMenuBar class="configurator-layout__menubar" :use-bordex-logo="false" :items="items" :mobile-items="mobileMenuItems">
            <template #end>
                <div v-if="isDebug" class="configurator-debug-info mr-3 items-center gap-2">
                    <Chip :label="`User: ${user?.name || 'Onbekend'}`" class="text-sm"/>
                    <Chip :label="`Role: ${userRoles}`" class="text-sm bg-orange-500"/>
                </div>
                <Button class="configurator-touch-target" @click="generateAssetsNow" v-if="!isConfiguratorUser">Generate Assets</Button>
                <Button
                    class="configurator-return-button configurator-return-button--desktop configurator-touch-target"
                    :label="t('configurator.share.done')"
                    icon="pi pi-check"
                    severity="primary"
                    @click="goToCallbackUrl"
                    v-if="isConfiguratorUser"
                />
                <Button
                    class="configurator-return-button configurator-return-button--compact configurator-touch-target"
                    :label="t('configurator.share.done')"
                    icon="pi pi-check"
                    severity="primary"
                    @click="goToCallbackUrl"
                    v-if="isConfiguratorUser"
                />
            </template>
        </CommonMenuBar>
        <DynamicDialog/>
        <main class="configurator-layout__main">
            <slot/>
        </main>
    </div>
</template>

<style scoped lang="scss">
.configurator-layout {
    box-sizing: border-box;
}

.configurator-layout__main {
    width: 100%;
    max-width: 100%;
    padding: 0;
    margin: 0;
    overflow-x: hidden;
}

@media screen and (min-width: 1025px) {
    .configurator-layout {
        height: 100dvh;
        max-height: 100dvh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .configurator-layout__menubar {
        flex: 0 0 auto;
    }

    .configurator-layout__main {
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
    }
}

.configurator-debug-info {
    display: none;
}

.configurator-return-button {
    border-radius: 12px !important;
    padding-block: 0.7rem !important;
    gap: 0.5rem !important;
    font-weight: 600 !important;
    letter-spacing: 0.01em !important;
    line-height: 1.2 !important;
    text-transform: uppercase !important;

    :deep(.p-button-icon) {
        font-size: 1.1rem !important;
    }
}

.configurator-layout :deep(.p-menubar-button) {
    display: none !important;
}

.configurator-return-button--desktop {
    display: none;
}

.configurator-return-button--compact {
    display: inline-flex;
}

@media screen and (max-width: 768px) {
    .configurator-layout__main {
        padding-inline: 0;
    }
}

@media screen and (min-width: 1025px) {
    .configurator-debug-info {
        display: flex;
    }

    .configurator-return-button--desktop {
        display: inline-flex;
    }

    .configurator-return-button--compact {
        display: none;
    }
}
</style>
